import { jitsiLocalStorage } from '@jitsi/js-utils';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { openDialog } from '../../../base/dialog';
import { getWindowWidth } from '../../../base/environment/utils';
import {
    FixedIcon,
    FloatingIcon,
    Icon,
    IconArrowRight,
    IconEventNote,
    IconFullScreen,
    IconMenuThumb,
    IconMicrophoneEmpty,
    IconParticipants,
    IconSettings
} from '../../../base/icons';
import { closeNotesPane, openNotesPane } from '../../../notes/actions';
import { getNotesPaneOpen } from '../../../notes/functions';
import { close, open } from '../../../participants-pane/actions';
import { getParticipantsPaneOpen } from '../../../participants-pane/functions';
import { SETTINGS_TABS, openSettingsDialog } from '../../../settings';
import { setFullScreen } from '../../../toolbox/actions.web';
import { VoiceCommandDialog } from '../../../voice-command/components/web/VoiceCommandDialog';
import {
    conferenceViewHandler
} from '../../actions.web';
import ConferenceTimer from '../ConferenceTimer';


const ConferenceDropdownMenu = props => {
    const [ isOpen, setIsOpen ] = useState(false);
    const [ activeSubmenu, setActiveSubmenu ] = useState(null);
    const fullScreenMode = useSelector(state => state['features/toolbox'].fullScreen);
    const dropdownRef = useRef(null);
    const _isParticipantsPaneVisible = useSelector(getParticipantsPaneOpen);
    const _isNotesPaneVisible = useSelector(getNotesPaneOpen);
    const Subject = props.subject;
    const { t } = useTranslation();

    useEffect(() => {
        const handleClickOutside = event => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setActiveSubmenu(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const conferenceViewStateHandler = state => {
        props.dispatch(conferenceViewHandler(state));
        jitsiLocalStorage.setItem('conferenceView', state);
    };

    const toggleFullScreen = () => {
        props.dispatch(setFullScreen(!fullScreenMode));
    };

    const toggleParticipantsPane = () => {
        _isParticipantsPaneVisible
            ? props.dispatch(close())
            : props.dispatch(open());
    };

    const toggleNotesPane = () => {
        _isNotesPaneVisible
            ? props.dispatch(closeNotesPane())
            : props.dispatch(openNotesPane());
    };

    const menuItems = [ ];

    getWindowWidth() < 730 && menuItems.push({
        id: 'subject',
        label: Subject,
        mobile: props.isMobile
    });

    const { indicators } = props;
    const shouldShowTimerInMenu = (indicators === 1 && getWindowWidth() < 350)
        || (indicators === 2 && getWindowWidth() < 400)
        || (indicators === 3 && getWindowWidth() < 440);

    (shouldShowTimerInMenu || (getWindowWidth() < 500 && props.joinanytime)) && menuItems.push({
        id: 'join-anytime',
        label: <ConferenceTimer />,
        mobile: props.isMobile
    });

    getWindowWidth() < 1171 && props._visibleButtons.has('voicecommand') && menuItems.push({
        id: 'voice-command',
        label: t('voicecommand.vcLabel'),
        icon: <Icon src = { IconMicrophoneEmpty } />,
        action: () => props.dispatch(openDialog(VoiceCommandDialog))
    });

    getWindowWidth() < 1250 && props._visibleButtons.has('participants-pane') && menuItems.push({
        id: 'participants',
        label: t('participantsPane.header'),
        icon: <Icon src = { IconParticipants } />,
        count: props._participantsLength || 1,
        action: () => toggleParticipantsPane()
    });

    // Notes is always available: it does not follow the configurable toolbar
    // buttons list.
    getWindowWidth() < 1250 && menuItems.push({
        id: 'notes',
        label: t('notesPane.title'),
        icon: <Icon src = { IconEventNote } />,
        action: () => toggleNotesPane()
    });

    getWindowWidth() < 1111 && getWindowWidth() > 805 && props._visibleButtons.has('fixed-floating') && menuItems.push(
        {
            id: 'view-mode',
            label: 'View Mode',

            // icon: <Icon src = { IconMonitor } />,
            hasSubmenu: true,
            submenu: [
                {
                    id: 'fixed-view',
                    label: t('settingsView.FixedView'),
                    icon: <Icon src = { FixedIcon } />,
                    action: () => conferenceViewStateHandler('fixed')
                },
                {
                    id: 'float-view',
                    label: t('settingsView.FloatingView'),
                    icon: <Icon src = { FloatingIcon } />,
                    action: () => conferenceViewStateHandler('float')
                }
            ]
        }
    );

    getWindowWidth() < 824 && props._visibleButtons.has('settings') && menuItems.push(
        {
            id: 'settings',
            label: t('settingsView.header'),
            icon: <Icon src = { IconSettings } />,
            action: () => props.dispatch(openSettingsDialog(SETTINGS_TABS.DEVICES))
        }
    );
    getWindowWidth() < 971 && props._visibleButtons.has('fullscreen') && menuItems.push(
        {
            id: 'fullscreen',
            label: t('toolbar.accessibilityLabel.fullScreen'),
            icon: <Icon src = { IconFullScreen } />,
            action: () => toggleFullScreen()
        }
    );


    const handleItemClick = item => {
        if (item.hasSubmenu) {
            setActiveSubmenu(activeSubmenu === item.id ? null : item.id);
        } else {
            item.action();
            setIsOpen(false);
            setActiveSubmenu(null);
        }
    };

    const handleSubmenuItemClick = submenuItem => {
        submenuItem.action();
        setIsOpen(false);
        setActiveSubmenu(null);
    };

    return (
        <div className = { `header-conference-dropdown-container ${props.className}` }>
            <div
                className = 'header-dropdown-wrapper'
                ref = { dropdownRef }>
                {/* Trigger Button */}
                <button
                    className = 'header-trigger-button label_buttons'
                    onClick = { () => setIsOpen(!isOpen) }>
                    <Icon src = { IconMenuThumb } />
                </button>

                {/* Dropdown Menu */}
                {isOpen && (
                    <div className = 'header-dropdown' >
                        {menuItems.map(item => (
                            <div key = { item.id }>
                                {/* Main Menu Item */}
                                <button
                                    className = 'header-menu-item'
                                    onClick = { () => handleItemClick(item) }>
                                    <div className = 'header-menu-item-content'>
                                        {item.icon}
                                        <span>{item.label}</span>
                                        {item.count
                      && <span className = 'header-count-badge'>{item.count}</span>
                                        }
                                    </div>
                                    {item.hasSubmenu && (
                                        <Icon
                                            className = { `header-chevron-right ${
                                                activeSubmenu === item.id ? 'open' : ''
                                            }` }
                                            size = { 16 }
                                            src = { IconArrowRight } />
                                    )}
                                </button>

                                {/* Submenu */}
                                {item.hasSubmenu && activeSubmenu === item.id && (
                                    <div className = 'header-submenu'>
                                        {item.submenu.map(submenuItem => (
                                            <button
                                                className = 'header-submenu-item'
                                                key = { submenuItem.id }
                                                onClick = { () => handleSubmenuItemClick(submenuItem) }>
                                                {submenuItem.icon}
                                                <span>{submenuItem.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConferenceDropdownMenu;
